import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  VStack,
  Text,
  Image,
  Badge,
  Select,
  Flex,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import { ExternalLinkIcon, ChevronRightIcon } from '@chakra-ui/icons';

import { getMarketplaceLogo } from 'components/marketplaceLogo';
import { useColors } from './colors';
import { useFormatDate } from 'contexts/TimezoneContext';
import {
  fulfillmentStatusBadge,
  orderMainStatusBadge,
  getOrderStatusTooltip,
  formatMoney,
} from '../utils/orderStatus.util';

const SkuLineItem = ({ item, order, textColor, mutedTextColor }) => (
  <VStack align="start" spacing={0}>
    {item.sku && (
      <Text fontSize="xs" color={textColor} fontFamily="mono" whiteSpace="nowrap">
        {item.sku}
      </Text>
    )}
    {order.marketplace === 'ebay' && item.itemId && (
      <Text fontSize="2xs" color={mutedTextColor} fontFamily="mono" whiteSpace="nowrap">
        Item {item.itemId}
      </Text>
    )}
  </VStack>
);

const OrderSkuCell = ({ order, textColor, mutedTextColor, isExpanded }) => {
  const items = order.lineItemSkus || [];
  if (!items.length) {
    return (
      <Text fontSize="sm" color={mutedTextColor}>
        —
      </Text>
    );
  }

  const hasMultiple = items.length > 1;
  const visibleItems = !hasMultiple || isExpanded ? items : [items[0]];

  return (
    <Box onClick={(e) => e.stopPropagation()} py={hasMultiple && isExpanded ? 1 : 0}>
      <VStack align="start" spacing={hasMultiple && isExpanded ? 2 : 1}>
        {visibleItems.map((item, index) => (
          <SkuLineItem
            key={`${item.sku || item.itemId || index}`}
            item={item}
            order={order}
            textColor={textColor}
            mutedTextColor={mutedTextColor}
          />
        ))}
        {hasMultiple && !isExpanded && (
          <Text fontSize="2xs" color="blue.400" fontWeight="medium">
            +{items.length - 1} more — hover to expand
          </Text>
        )}
      </VStack>
    </Box>
  );
};

const marketplaceOrderUrl = (order) => {
  if (order.marketplace === 'ebay') {
    return `https://www.ebay.com/sh/ord/details?orderid=${order.marketplaceOrderId}`;
  }
  if (order.marketplace === 'amazon') {
    return `https://sellercentral.amazon.com/orders-v3/order/${order.marketplaceOrderId}`;
  }
  return null;
};

const OrdersTable = ({
  orders,
  sortBy,
  setSortBy,
  pageSize,
  setPageSize,
  currentPage,
  totalOrders,
}) => {
  const navigate = useNavigate();
  const { formatDateTime, formatDate } = useFormatDate();
  const [expandedSkuOrderId, setExpandedSkuOrderId] = useState(null);
  const {
    borderColor,
    headerBg,
    hoverBg,
    textColor,
    mutedTextColor,
    inputColor,
    filterBg,
    filterOptionHover,
    filterOptionChecked,
    filterHover,
    filterChecked,
  } = useColors();

  const selectProps = {
    sx: {
      option: { bg: filterBg, color: inputColor },
      '> option:hover': { bg: `${filterOptionHover}!important` },
      '> option:checked': { bg: `${filterOptionChecked}!important` },
    },
    _hover: { bg: filterHover },
    _focus: { bg: filterChecked, borderColor: 'blue.300' },
  };

  const start = orders.length ? (currentPage - 1) * pageSize + 1 : 0;
  const end = Math.min(currentPage * pageSize, totalOrders);

  return (
    <VStack spacing={4} align="stretch">
      <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={4}>
          <Text fontSize="sm" color={mutedTextColor}>
            {orders.length > 0 ? `${start} – ${end} of ${totalOrders}` : '0 orders'}
          </Text>
          <Select size="sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)} w="280px" {...selectProps}>
            <option value="placedAt-">Placed: newest first</option>
            <option value="placedAt+">Placed: oldest first</option>
            <option value="addedAt-">Added: newest first</option>
            <option value="addedAt+">Added: oldest first</option>
            <option value="total-">Total: high to low</option>
            <option value="total+">Total: low to high</option>
            <option value="shipBy+">Ship by: soonest</option>
          </Select>
        </HStack>
        <Select
          size="sm"
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          w="140px"
          {...selectProps}
        >
          <option value={10}>10 per page</option>
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
        </Select>
      </Flex>

      <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead bg={headerBg}>
            <Tr>
              <Th whiteSpace="nowrap">Order</Th>
              <Th minW="180px">Store</Th>
              <Th maxW="100px" w="100px">
                Buyer
              </Th>
              <Th isNumeric>Items</Th>
              <Th>SKU</Th>
              <Th isNumeric>Total</Th>
              <Th>Status</Th>
              <Th>Fulfillment</Th>
              <Th>Ship to</Th>
              <Th whiteSpace="nowrap">Order placed</Th>
              <Th w="40px" />
            </Tr>
          </Thead>
          <Tbody>
            {orders.map((order) => {
              const status = orderMainStatusBadge(order);
              const statusTooltip = getOrderStatusTooltip(order);
              const fulfillment = fulfillmentStatusBadge(order.orderFulfillmentStatus);
              const extUrl = marketplaceOrderUrl(order);
              const isSkuExpanded = expandedSkuOrderId === order.id;
              const hasMultipleSkus = (order.lineItemSkus?.length || 0) > 1;

              return (
                <Tr
                  key={order.id}
                  _hover={{ bg: hoverBg }}
                  onMouseEnter={hasMultipleSkus ? () => setExpandedSkuOrderId(order.id) : undefined}
                  onMouseLeave={hasMultipleSkus ? () => setExpandedSkuOrderId(null) : undefined}
                  verticalAlign={isSkuExpanded ? 'top' : 'middle'}
                  transition="height 0.15s ease"
                >
                  <Td whiteSpace="nowrap" verticalAlign={isSkuExpanded ? 'top' : 'middle'}>
                    <HStack spacing={2} align="start">
                      <Image
                        src={getMarketplaceLogo(order.marketplace)}
                        alt={order.marketplace}
                        boxSize="22px"
                        objectFit="contain"
                        mt={0.5}
                        flexShrink={0}
                      />
                      <VStack align="start" spacing={0}>
                        <HStack spacing={1}>
                          <Text
                            fontWeight="semibold"
                            fontSize="sm"
                            color={textColor}
                            fontFamily="mono"
                            whiteSpace="nowrap"
                          >
                            {order.marketplaceOrderId}
                          </Text>
                          {extUrl && (
                            <IconButton
                              as="a"
                              href={extUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Open on marketplace"
                              icon={<ExternalLinkIcon />}
                              size="xs"
                              variant="ghost"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </HStack>
                        <HStack spacing={1} flexWrap="wrap">
                          {order.isPrime && (
                            <Badge colorScheme="blue" variant="subtle" fontSize="2xs">
                              Prime
                            </Badge>
                          )}
                          {order.isBusinessOrder && (
                            <Badge colorScheme="purple" variant="subtle" fontSize="2xs">
                              Business
                            </Badge>
                          )}
                          {order.salesRecordReference && (
                            <Text fontSize="2xs" color={mutedTextColor}>
                              SR {order.salesRecordReference}
                            </Text>
                          )}
                        </HStack>
                      </VStack>
                    </HStack>
                  </Td>
                  <Td minW="180px" verticalAlign={isSkuExpanded ? 'top' : 'middle'}>
                    <Text fontSize="sm" color={textColor}>
                      {order.storeName}
                    </Text>
                    <Text fontSize="2xs" color={mutedTextColor} textTransform="uppercase">
                      {order.fulfillmentChannel}
                    </Text>
                  </Td>
                  <Td maxW="100px" w="100px" verticalAlign={isSkuExpanded ? 'top' : 'middle'}>
                    <Tooltip
                      label={
                        order.buyerName && order.buyerUsername
                          ? `${order.buyerName} (@${order.buyerUsername})`
                          : order.buyerName || order.buyerUsername || '—'
                      }
                      hasArrow
                    >
                      <Text fontSize="xs" color={textColor} noOfLines={1}>
                        {order.buyerUsername
                          ? `@${order.buyerUsername}`
                          : order.buyerName || '—'}
                      </Text>
                    </Tooltip>
                  </Td>
                  <Td isNumeric verticalAlign={isSkuExpanded ? 'top' : 'middle'}>
                    <Text fontSize="sm" color={textColor}>
                      {order.lineItemCount}
                    </Text>
                    <Text fontSize="2xs" color={mutedTextColor}>
                      {order.quantityTotal} qty
                    </Text>
                  </Td>
                  <Td maxW="220px" verticalAlign="top">
                    <OrderSkuCell
                      order={order}
                      textColor={textColor}
                      mutedTextColor={mutedTextColor}
                      isExpanded={isSkuExpanded}
                    />
                  </Td>
                  <Td isNumeric verticalAlign={isSkuExpanded ? 'top' : 'middle'}>
                    <Text fontWeight="semibold" fontSize="sm" color={textColor}>
                      {formatMoney(order.orderTotal, order.currency)}
                    </Text>
                  </Td>
                  <Td verticalAlign={isSkuExpanded ? 'top' : 'middle'}>
                    <Tooltip
                      label={
                        statusTooltip ? (
                          <Box whiteSpace="pre-line">{statusTooltip}</Box>
                        ) : (
                          status.label
                        )
                      }
                      hasArrow
                      isDisabled={!statusTooltip}
                      openDelay={200}
                    >
                      <Badge colorScheme={status.colorScheme} variant="subtle" fontSize="xs">
                        {status.label}
                      </Badge>
                    </Tooltip>
                  </Td>
                  <Td verticalAlign={isSkuExpanded ? 'top' : 'middle'}>
                    <Tooltip label={order.orderFulfillmentStatus || order.orderStatus} hasArrow>
                      <Badge colorScheme={fulfillment.colorScheme} variant="subtle" fontSize="xs">
                        {fulfillment.label}
                      </Badge>
                    </Tooltip>
                    {order.shipByDate && (
                      <Text fontSize="2xs" color={mutedTextColor} mt={0.5}>
                        Ship by {formatDate(order.shipByDate, { format: 'MMM D' })}
                      </Text>
                    )}
                  </Td>
                  <Td verticalAlign={isSkuExpanded ? 'top' : 'middle'}>
                    {order.shipTo ? (
                      <Text fontSize="xs" color={textColor} noOfLines={2}>
                        {order.shipTo.city}, {order.shipTo.stateOrProvince}
                        <Text as="span" color={mutedTextColor}>
                          {' '}
                          {order.shipTo.countryCode}
                        </Text>
                      </Text>
                    ) : (
                      '—'
                    )}
                  </Td>
                  <Td whiteSpace="nowrap" verticalAlign={isSkuExpanded ? 'top' : 'middle'}>
                    <Text fontSize="xs" color={textColor}>
                      {formatDateTime(order.placedAt)}
                    </Text>
                  </Td>
                  <Td verticalAlign={isSkuExpanded ? 'top' : 'middle'}>
                    <IconButton
                      aria-label="View order details"
                      icon={<ChevronRightIcon />}
                      size="sm"
                      variant="ghost"
                      color={mutedTextColor}
                      onClick={() => navigate(`/admin/orders/${order.id}`)}
                    />
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </VStack>
  );
};

export default OrdersTable;
